/**
 * @fileoverview Modal for editing the orphan policy of an existing relationship in the manifest.
 * @module system-web/components/migration/manifest-editor/EditRelationshipModal
 *
 * Responsibilities:
 * - Allow an admin to modify the orphanPolicy and orphanPolicyJustification of a relationship.
 * - For manifest-only (addedRelationships) entries, allow cardinality changes as well.
 * - Confirm the changes, which are written to modifiedRelationships[] (for baseline rels)
 *   or directly updated in addedRelationships[] (for manifest-only rels).
 *
 * Dependencies:
 * - lucide-react (icons)
 * - ManifestRelationshipsGrid (EffectiveRelationship)
 *
 * Architecture:
 * - Overlay modal pattern (fixed inset-0 bg-black/50).
 * - Only orphan policy is editable for baseline relationships (enforcement is immutable).
 * - Full edit (cardinality + orphan policy) is available for added (manifest-only) relationships.
 * - Confirmed payload is bubbled up to parent via onConfirm.
 *
 * Notes:
 * - This modal never makes API calls directly; the parent calls saveOverride after receiving the payload.
 *
 * @reusable
 * Usage: Opened from the Edit button in ManifestRelationshipsGrid.
 * Rules: stateful (form state); delegates mutation to parent via onConfirm.
 */

import React, { useState } from 'react';
import { X, Loader2, AlertTriangle, Edit2 } from 'lucide-react';
import type { EffectiveRelationship } from './ManifestRelationshipsGrid';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract EditRelationshipPayload
 *
 * Purpose:
 * - The output of the EditRelationshipModal upon confirmation.
 *
 * Fields:
 * - matchKey: Identifies the relationship being modified.
 * - orphanPolicy: The new orphan policy.
 * - orphanPolicyJustification: Required when orphanPolicy !== 'block'.
 * - cardinality: New cardinality — only editable for isAdded relationships.
 *
 * Rules:
 * - For baseline rels: matchKey maps to a modifiedRelationships entry.
 * - For added rels: matchKey maps to an addedRelationships entry for direct update.
 *
 * Sync:
 * - Delivered to parent; parent updates override and calls saveOverride.
 */
export interface EditRelationshipPayload {
  matchKey: string;
  orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot';
  orphanPolicyJustification: string;
  cardinality: 'one-to-one' | 'one-to-many';
}

/**
 * @contract EditRelationshipModalProps
 *
 * Purpose:
 * - Props for the EditRelationshipModal component.
 *
 * Fields:
 * - relationship: The current relationship being edited.
 * - onConfirm: Called with the completed payload when the user submits.
 * - onCancel: Called when the user dismisses the modal.
 *
 * Rules:
 * - onConfirm may throw; the modal catches errors and shows them inline.
 *
 * Sync:
 * - Not applicable.
 */
export interface EditRelationshipModalProps {
  relationship: EffectiveRelationship;
  onConfirm: (payload: EditRelationshipPayload) => Promise<void>;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ORPHAN_POLICY_OPTIONS: { value: EditRelationshipPayload['orphanPolicy']; label: string; description: string }[] = [
  { value: 'block',          label: 'Block',         description: 'Fail the migration if orphans exist' },
  { value: 'warn',           label: 'Warn',          description: 'Log warnings; continue migration' },
  { value: 'drop',           label: 'Drop',          description: 'Silently skip orphaned child rows' },
  { value: 'includeAsRoot',  label: 'Include Root',  description: 'Migrate orphans as root documents' },
];

const CARDINALITY_OPTIONS: { value: EditRelationshipPayload['cardinality']; label: string }[] = [
  { value: 'one-to-many', label: 'One-to-Many (1:N)' },
  { value: 'one-to-one',  label: 'One-to-One (1:1)' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component EditRelationshipModal
 *
 * Purpose:
 * - Overlay modal for editing the orphan policy (and cardinality for manifest-only rels)
 *   of an existing manifest relationship.
 *
 * Props:
 * - See EditRelationshipModalProps.
 *
 * Behavior:
 * - Shows the relationship endpoints as read-only text.
 * - Cardinality selector enabled only for isAdded relationships.
 * - Orphan policy selector for all relationships.
 * - Justification textarea appears when orphanPolicy !== 'block'.
 * - Validate on submit: justification required when non-block.
 * - Calls onConfirm with the payload; shows errors inline if onConfirm throws.
 *
 * Data Source:
 * - Props only; no data fetching.
 *
 * Constraints:
 * - No Dexie reads or writes; no API calls.
 *
 * @reusable
 * Usage: Opened by ManifestEditorTab on Edit button click from ManifestRelationshipsGrid.
 * Rules: stateful (form state); delegates mutation to parent via onConfirm.
 */
export function EditRelationshipModal({
  relationship,
  onConfirm,
  onCancel,
}: EditRelationshipModalProps) {
  const [orphanPolicy, setOrphanPolicy] = useState<EditRelationshipPayload['orphanPolicy']>(
    relationship.orphanPolicy,
  );
  const [justification, setJustification] = useState(
    relationship.orphanPolicyJustification ?? '',
  );
  const [cardinality, setCardinality] = useState<EditRelationshipPayload['cardinality']>(
    relationship.cardinality,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (orphanPolicy !== 'block' && !justification.trim()) {
      errs.justification = 'Justification is required for non-block orphan policies.';
    }
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({
        matchKey: relationship.matchKey,
        orphanPolicy,
        orphanPolicyJustification: justification.trim(),
        cardinality,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save changes');
      setIsSubmitting(false);
    }
  }

  const { parent, child } = relationship;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center gap-2">
            <Edit2 className="h-4 w-4 text-gray-500" />
            <h2 className="text-base font-semibold">Edit Relationship</h2>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">

          {/* Relationship identity (read-only) */}
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs font-mono">
            <span className="text-gray-600">{parent.table}</span>
            <span className="text-gray-400 mx-1">.</span>
            <span className="font-semibold">{parent.column}</span>
            <span className="mx-1.5 text-gray-400">→</span>
            <span className="text-gray-600">{child.table}</span>
            <span className="text-gray-400 mx-1">.</span>
            <span className="font-semibold">{child.column}</span>
          </div>

          {/* Cardinality (editable only for added rels) */}
          <div>
            <label htmlFor="editCardinality" className="block text-xs font-medium mb-1">
              Cardinality
              {!relationship.isAdded && (
                <span className="ml-1 text-gray-400 font-normal">(read-only for baseline relationships)</span>
              )}
            </label>
            <select
              id="editCardinality"
              value={cardinality}
              onChange={(e) => setCardinality(e.target.value as typeof cardinality)}
              disabled={!relationship.isAdded}
              className="w-full rounded border px-2 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: 'rgba(0,0,0,0.18)' }}
            >
              {CARDINALITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Orphan policy */}
          <div>
            <label htmlFor="editOrphanPolicy" className="block text-xs font-medium mb-1">Orphan Policy</label>
            <select
              id="editOrphanPolicy"
              value={orphanPolicy}
              onChange={(e) => setOrphanPolicy(e.target.value as typeof orphanPolicy)}
              className="w-full rounded border px-2 py-1.5 text-sm"
              style={{ borderColor: 'rgba(0,0,0,0.18)' }}
            >
              {ORPHAN_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label} — {o.description}</option>
              ))}
            </select>
          </div>

          {/* Justification */}
          {orphanPolicy !== 'block' && (
            <div>
              <label htmlFor="editJustification" className="block text-xs font-medium mb-1">
                Orphan Policy Justification <span className="text-red-500">*</span>
              </label>
              <textarea
                id="editJustification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                className={`w-full rounded border px-2 py-1.5 text-sm resize-none ${validationErrors.justification ? 'border-red-400' : ''}`}
                style={{ borderColor: validationErrors.justification ? undefined : 'rgba(0,0,0,0.18)' }}
                placeholder="Explain why this orphan policy is safe for migration."
              />
              {validationErrors.justification && (
                <p className="text-xs text-red-600 mt-0.5">{validationErrors.justification}</p>
              )}
            </div>
          )}

          {/* Submit error */}
          {submitError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end px-5 py-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50"
            style={{ background: '#2563eb' }}
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
